import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { stores } from '@migrate/stores'
import UserNotificationStore, { UserNotificationType } from '@migrate/stores/UserNotificationStore'
import SecurityStore from '@migrate/stores/SecurityStore'
import i18n from 'i18next'
import { router } from '@shared/router/router'

const ERROR_MESSAGE = 'common.error.request.generic'
const FORBIDDEN_HEADER_NAME = 'x-mercatus-forbidden'

const TIMEZONE_HEADER_NAME = 'X-Mercatus-TimeZone'
const OFFSET_MINUTES_HEADER_NAME = 'X-Mercatus-Offset-Minutes'
const LOCALE_HEADER_NAME = 'X-Mercatus-Locale'

export interface IMercatusAxiosRequestConfig extends InternalAxiosRequestConfig {
  mercatusNotifyOnError?: boolean
  ignore403?: boolean
  ignore404?: boolean
  sendCsrfToken?: boolean
  retryCount?: number
}

// CRPM-1068 Error codes to be ignored and not generate notifications.
// Currently, when the user logs/times out, a bunch of requests can 401/403. It is generally
// pointless to generate generic error messages for these anyway since the system should respond
// by displaying the login screen or a 403 page when those occur anyway.
const IGNORE_NOTIFY_ERROR_STATUS_CODES = [401, 403]

export const mercatusRequestInterceptor = (request: InternalAxiosRequestConfig<any>) => {
  const mercatusRequest = request as IMercatusAxiosRequestConfig

  // Set default values for `IMercatusAxiosRequestConfig` if not specified
  if (mercatusRequest.mercatusNotifyOnError === undefined) {
    mercatusRequest.mercatusNotifyOnError = true
  }
  if (mercatusRequest.ignore403 === undefined) {
    mercatusRequest.ignore403 = false
  }
  if (mercatusRequest.sendCsrfToken === undefined) {
    mercatusRequest.sendCsrfToken = true
  }
  if (mercatusRequest.retryCount === undefined) {
    mercatusRequest.retryCount = 0
  }
  mercatusRequest.params = {
    ...(mercatusRequest.params || {}),
    _client: 'IlmNext',
  }

  mercatusRequest.headers.set(
    TIMEZONE_HEADER_NAME,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  )
  mercatusRequest.headers.set(OFFSET_MINUTES_HEADER_NAME, `${new Date().getTimezoneOffset() * -1}`)
  mercatusRequest.headers.set(LOCALE_HEADER_NAME, i18n.language)

  return SecurityStore.addRequestHeadersIfRequired(mercatusRequest)
}
export const mercatusResponseInterceptor = (response: AxiosResponse) => {
  return response
}

export const mercatusResponseErrorInterceptor = (error: AxiosError) => {
  const mercatusRequest = error.config as IMercatusAxiosRequestConfig
  // Send CSRF request headers for 403 with specified header.
  if (
    error.response &&
    error.response.status === 403 &&
    error.response.headers[FORBIDDEN_HEADER_NAME] === 'Token' &&
    mercatusRequest.retryCount! < 1
  ) {
    mercatusRequest.retryCount!++
    return SecurityStore.load().then(() => axios.request(mercatusRequest))

    // All other 403s redirect to 403 page.
  } else if (error.response && error.response.status === 403 && !mercatusRequest.ignore403) {
    router.navigate({
      to: '/403',
    })
  } else if (error.response && error.response.status === 404 && !mercatusRequest.ignore404) {
    if (error.response.headers[FORBIDDEN_HEADER_NAME] === 'NoSuchEntity') {
      router.navigate({
        to: '/403',
        search: {
          reason: 'entity',
        },
      })
    } else {
      router.navigate({
        to: '/404',
      })
    }
    // All other errors display generic error notification, feature can be disabled by setting
    // `mercatusNotifyOnError: false` in the Axios request config object.
  } else if (
    mercatusRequest.mercatusNotifyOnError &&
    !IGNORE_NOTIFY_ERROR_STATUS_CODES.includes(error.response?.status ?? -1)
  ) {
    const notificationStore: UserNotificationStore = stores[UserNotificationStore.STORE_NAME]
    if (notificationStore) {
      notificationStore.addNotification(UserNotificationType.ERROR, ERROR_MESSAGE)
    }
  }
  return Promise.reject(error)
}

export const initAxios = (axiosInstance: AxiosInstance = axios) => {
  // @ts-expect-error handlers is axios internal state and is not on the public interceptor type
  if (axiosInstance.interceptors.request.handlers.length === 0) {
    axiosInstance.interceptors.request.use(mercatusRequestInterceptor)
    axiosInstance.interceptors.response.use(
      mercatusResponseInterceptor,
      mercatusResponseErrorInterceptor,
    )
  }
}
